import axios from "axios";
import { API_BASE_URL } from "../utils/apiConfig";
import { createRefreshInterceptor } from "./tokenRefresh";
import { tokenStorage } from "./tokenStorage";
import { tenantEntryStorage } from "./tenantEntryStorage";

// Create axios instance with auth + shared refresh interceptor
const createApiInstance = (basePath) => {
  const instance = axios.create({
    baseURL: `${API_BASE_URL}${basePath}`,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Add request interceptor to include auth token
  instance.interceptors.request.use(
    (config) => {
      const token = tokenStorage.getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const enteredTenant = tenantEntryStorage.get();
      if (enteredTenant?.id) {
        config.headers["X-Tenant-ID"] = enteredTenant.id;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor — shared refresh queue prevents race conditions
  instance.interceptors.response.use(
    (response) => response,
    createRefreshInterceptor(instance)
  );

  return instance;
};

const api = createApiInstance("/config");
const fileApiInstance = createApiInstance("/files");
const folderApiInstance = createApiInstance("/folders");
const ragApiInstance = createApiInstance("/rag");
const storeLocationsApiInstance = createApiInstance("/store-locations");
const warehouseLocationsApiInstance = createApiInstance("/warehouse-locations");
const chatApiInstance = createApiInstance("/chats");

export const configApi = {
  // Get all configurations
  getAllConfigs: async (chatId = null) => {
    const params = chatId ? { chat_id: chatId } : {};
    const response = await api.get("/", { params });
    return response.data;
  },

  // Get specific configuration
  getConfig: async (configType, chatId = null) => {
    const params = chatId ? { chat_id: chatId } : {};
    const response = await api.get(`/${configType}`, { params });
    return response.data;
  },

  // Update specific configuration
  updateConfig: async (configType, data, chatId = null) => {
    // Include chat_id in the data if provided
    const requestData = chatId ? { ...data, chat_id: chatId } : data;
    const response = await api.put(`/${configType}`, { data: requestData });
    return response.data;
  },
};

export const fileApi = {
  // List all files
  listFiles: async (collectionName = null, chatId = null, options = {}, signal = null) => {
    const params = {};
    if (collectionName) params.collection_name = collectionName;
    if (chatId) params.chat_id = chatId;
    // Pagination
    if (options.page) params.page = options.page;
    if (options.pageSize) params.page_size = options.pageSize;
    // Filters
    if (options.name) params.name = options.name;
    if (options.fileType) params.file_type = options.fileType;
    if (options.createdBy) params.created_by = options.createdBy;
    if (options.lastUpdatedBy) params.last_updated_by = options.lastUpdatedBy;
    if (options.createdAtStart) params.created_at_start = options.createdAtStart;
    if (options.createdAtEnd) params.created_at_end = options.createdAtEnd;
    if (options.updatedAtStart) params.updated_at_start = options.updatedAtStart;
    if (options.updatedAtEnd) params.updated_at_end = options.updatedAtEnd;
    if (options.campaignStartFrom) params.campaign_start_from = options.campaignStartFrom;
    if (options.campaignStartTo) params.campaign_start_to = options.campaignStartTo;
    if (options.campaignEndFrom) params.campaign_end_from = options.campaignEndFrom;
    if (options.campaignEndTo) params.campaign_end_to = options.campaignEndTo;
    if (options.campaignStatus) params.campaign_status = options.campaignStatus;
    if (options.chatId) params.chat_id = options.chatId;
    // Folder filter
    if (options.folderId !== undefined) params.folder_id = options.folderId;
    // Cursor
    if (options.cursor) params.cursor = options.cursor;
    // Sorting
    if (options.sortBy) params.sort_by = options.sortBy;
    if (options.sortOrder) params.sort_order = options.sortOrder;
    const response = await fileApiInstance.get("/list-files", { params, signal });
    return response.data;
  },

  // Upload file
  uploadFile: async (
    file,
    metadata = null,
    collectionName = null,
    aiAgentKnowledgeStartDate = null,
    aiAgentKnowledgeEndDate = null,
    chatIds = null,
    folderId = null,
    sourceUrls = null
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    if (metadata) {
      formData.append("metadata", JSON.stringify(metadata));
    }
    if (collectionName) {
      formData.append("collection_name", collectionName);
    }
    if (aiAgentKnowledgeStartDate) {
      formData.append(
        "ai_agent_knowledge_start_date",
        aiAgentKnowledgeStartDate
      );
    }
    if (aiAgentKnowledgeEndDate) {
      formData.append("ai_agent_knowledge_end_date", aiAgentKnowledgeEndDate);
    }
    // Always send chat_ids, even if empty array (to prevent backend fallback)
    if (chatIds !== null && chatIds !== undefined) {
      formData.append("chat_ids", JSON.stringify(chatIds));
    }
    if (folderId) {
      formData.append("folder_id", folderId);
    }
    if (sourceUrls && sourceUrls.length > 0) {
      // Only sent when there is at least one link. Absent/[] = no change on upload.
      formData.append("source_urls", JSON.stringify(sourceUrls));
    }

    const response = await fileApiInstance.post("/upload-file", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // Update file metadata.
  // sourceUrls semantics:
  //   undefined -> field not sent, server preserves existing links
  //   []        -> remove all links
  //   [...]     -> replace the list
  updateFileMetadata: async (
    filePath,
    aiAgentKnowledgeStartDate = null,
    aiAgentKnowledgeEndDate = null,
    collectionName,
    sourceUrls = undefined
  ) => {
    const formData = new FormData();
    formData.append("collection_name", collectionName);
    if (aiAgentKnowledgeStartDate) {
      formData.append(
        "ai_agent_knowledge_start_date",
        aiAgentKnowledgeStartDate
      );
    }
    if (aiAgentKnowledgeEndDate) {
      formData.append("ai_agent_knowledge_end_date", aiAgentKnowledgeEndDate);
    }
    if (sourceUrls !== undefined) {
      // An empty array is a valid "remove all" intent and must be sent.
      formData.append("source_urls", JSON.stringify(sourceUrls));
    }

    const response = await fileApiInstance.put(
      `/file-metadata/${encodeURIComponent(filePath)}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Backward compatibility alias
  updateCampaignMetadata: async (
    filePath,
    aiAgentKnowledgeStartDate = null,
    aiAgentKnowledgeEndDate = null
  ) => {
    return fileApi.updateFileMetadata(
      filePath,
      aiAgentKnowledgeStartDate,
      aiAgentKnowledgeEndDate,
      "campaigns"
    );
  },

  // Delete file
  deleteFile: async (filePath, collectionName = null, chatId = null) => {
    const params = {};
    if (collectionName) params.collection_name = collectionName;
    // A loja para a qual uma campanha da VTEX é rejeitada. O ficheiro é partilhado
    // pelas lojas, portanto apagar sem dizer qual não tem significado.
    if (chatId) params.chat_id = chatId;
    const response = await fileApiInstance.delete(`/file/${encodeURIComponent(filePath)}`, { params });
    return response.data;
  },

  // Get file content
  getFileContent: async (filePath, collectionName = null) => {
    const params = collectionName ? { collection_name: collectionName } : {};
    const response = await fileApiInstance.get(
      `/file/${encodeURIComponent(filePath)}`,
      { params }
    );
    return response.data;
  },

  // Get file content as blob (for PDFs)
  getFileContentAsBlob: async (filePath, collectionName = null) => {
    const params = collectionName ? { collection_name: collectionName } : {};
    const response = await fileApiInstance.get(
      `/file/${encodeURIComponent(filePath)}`,
      {
        responseType: "blob",
        headers: {
          Accept: "application/pdf,application/octet-stream,*/*",
        },
        params,
      }
    );
    return response.data;
  },

  // Update file content
  updateFileContent: async (filePath, content, collectionName = null) => {
    const formData = new FormData();
    const blob = new Blob([content], { type: "text/plain" });
    formData.append("file", blob, filePath);
    if (collectionName) {
      formData.append("collection_name", collectionName);
    }

    const response = await fileApiInstance.put(
      `/file/${encodeURIComponent(filePath)}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Add chat to file
  addChatToFile: async (filePath, chatId, collectionName) => {
    const formData = new FormData();
    formData.append('file_path', filePath);
    formData.append('chat_id', chatId);
    formData.append('collection_name', collectionName);
    
    const response = await fileApiInstance.post('/add-to-chat', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Re-index all files with new embeddings
  reindexAll: async () => {
    const response = await fileApiInstance.post("/reindex-all");
    return response.data;
  },

  // Move files to a folder
  moveFiles: async (filePaths, folderId, collectionName) => {
    const response = await fileApiInstance.post("/move", {
      file_paths: filePaths,
      folder_id: folderId || null,
      collection_name: collectionName,
    });
    return response.data;
  },

  // Remove chat from file
  removeChatFromFile: async (filePath, chatId, collectionName) => {
    const formData = new FormData();
    formData.append('file_path', filePath);
    formData.append('chat_id', chatId);
    formData.append('collection_name', collectionName);
    
    const response = await fileApiInstance.post('/remove-from-chat', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
};

export const folderApi = {
  createFolder: async (collection, name, parentFolderId = null) => {
    const response = await folderApiInstance.post("", {
      collection,
      name,
      parent_folder_id: parentFolderId,
    });
    return response.data;
  },

  listFolders: async (collection, parentFolderId = null, options = {}) => {
    const params = { collection };
    if (parentFolderId) params.parent_folder_id = parentFolderId;
    if (options.cursor) params.cursor = options.cursor;
    if (options.limit) params.limit = options.limit;
    if (options.sortBy) params.sort_by = options.sortBy;
    if (options.sortOrder) params.sort_order = options.sortOrder;
    const response = await folderApiInstance.get("", { params });
    return response.data;
  },

  getFolder: async (folderId) => {
    const response = await folderApiInstance.get(`/${folderId}`);
    return response.data;
  },

  renameFolder: async (folderId, name) => {
    const response = await folderApiInstance.put(`/${folderId}`, { name });
    return response.data;
  },

  deleteFolder: async (folderId) => {
    const response = await folderApiInstance.delete(`/${folderId}`);
    return response.data;
  },

  moveFolder: async (folderId, newParentFolderId = null) => {
    const response = await folderApiInstance.post(`/${folderId}/move`, {
      new_parent_folder_id: newParentFolderId,
    });
    return response.data;
  },

  getFolderBreadcrumbs: async (folderId) => {
    const response = await folderApiInstance.get(`/${folderId}/breadcrumbs`);
    return response.data;
  },

  getFolderContentsCount: async (folderId) => {
    const response = await folderApiInstance.get(`/${folderId}/contents-count`);
    return response.data;
  },

  searchFolders: async (collection, query) => {
    const response = await folderApiInstance.get("/search", {
      params: { collection, q: query },
    });
    return response.data;
  },

  // Bind/clear the Zitadel role required to access a top-level
  // internal_procedures folder (admin-only; backend validates reserved keys).
  // Pass null to clear the binding (folder becomes open to all
  // internal_procedures holders).
  setFolderRole: async (folderId, requiredRole) => {
    const response = await folderApiInstance.put(`/${folderId}/required-role`, {
      required_role: requiredRole,
    });
    return response.data;
  },
};

export const ragApi = {
  // Products similarity search
  productsSimilaritySearch: async (data, chatId = null) => {
    const params = chatId ? { chat_id: chatId } : {};
    const response = await ragApiInstance.post(
      "/products-similarity-search",
      data,
      { params }
    );
    return response.data;
  },

  // General information and campaigns similarity search
  similaritySearch: async (data, chatId = null) => {
    const params = chatId ? { chat_id: chatId } : {};
    const response = await ragApiInstance.post("/similarity-search", data, { params });
    return response.data;
  },

  // VTEX promotion sync (RPAA-229). O chat_id é obrigatório e sem default nos três:
  // cada chat é uma loja com o seu canal de venda, e sincronizar "o chat default"
  // é o erro que este trabalho existe para não cometer.
  getVtexPromotionSync: async (chatId) => {
    const response = await ragApiInstance.get("/vtex-promotions", {
      params: { chat_id: chatId },
    });
    return response.data;
  },

  // Dispara a sincronização em background. Responde "started" de imediato — não há
  // endpoint de progresso; recarregar a vista é como se vê o resultado.
  syncVtexPromotions: async (chatId) => {
    const response = await ragApiInstance.post("/sync-vtex-promotions", null, {
      params: { chat_id: chatId },
    });
    return response.data;
  },

  // Anula uma rejeição. A promoção só volta na sincronização SEGUINTE.
  acceptVtexPromotion: async (chatId, promotionId) => {
    const response = await ragApiInstance.post("/vtex-promotions/accept", null, {
      params: { chat_id: chatId, promotion_id: promotionId },
    });
    return response.data;
  },
};

export const storeLocationsApi = {
  // Import store locations from JSON file
  importStores: async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await storeLocationsApiInstance.post("/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // Get all stores
  getAllStores: async (includeInactive = false) => {
    const params = includeInactive ? { include_inactive: true } : {};
    const response = await storeLocationsApiInstance.get("/", { params });
    return response.data;
  },

  // Get store by ID
  getStore: async (storeId) => {
    const response = await storeLocationsApiInstance.get(`/${storeId}`);
    return response.data;
  },

  // Create store
  createStore: async (storeData) => {
    const response = await storeLocationsApiInstance.post("/", storeData);
    return response.data;
  },

  // Update store
  updateStore: async (storeId, storeData) => {
    const response = await storeLocationsApiInstance.put(
      `/${storeId}`,
      storeData
    );
    return response.data;
  },

  // Delete store
  deleteStore: async (storeId) => {
    const response = await storeLocationsApiInstance.delete(`/${storeId}`);
    return response.data;
  },

  // Toggle store active status
  toggleStoreActive: async (storeId) => {
    const response = await storeLocationsApiInstance.patch(
      `/${storeId}/toggle-active`
    );
    return response.data;
  },

  // Export stores to JSON file
  exportStores: async () => {
    const response = await storeLocationsApiInstance.get("/export", {
      responseType: "blob",
    });

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers["content-disposition"];
    const nowDate = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
    let filename = `lojas_${nowDate}.json`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and download
    const blob = new Blob([response.data], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },

  // Check store stock for a specific product
  checkStoreStock: async (storeId, productId) => {
    const response = await storeLocationsApiInstance.post(`/${storeId}/stock`, {
      product_id: productId,
    });
    return response.data;
  },
};

export const warehouseLocationsApi = {
  getAllWarehouses: async (includeInactive = false) => {
    const params = includeInactive ? { include_inactive: true } : {};
    const response = await warehouseLocationsApiInstance.get("/", { params });
    return response.data;
  },

  getWarehouse: async (centerId) => {
    const response = await warehouseLocationsApiInstance.get(`/${centerId}`);
    return response.data;
  },

  createWarehouse: async (data) => {
    const response = await warehouseLocationsApiInstance.post("/", data);
    return response.data;
  },

  updateWarehouse: async (centerId, data) => {
    const response = await warehouseLocationsApiInstance.put(`/${centerId}`, data);
    return response.data;
  },

  deleteWarehouse: async (centerId) => {
    const response = await warehouseLocationsApiInstance.delete(`/${centerId}`);
    return response.data;
  },

  toggleWarehouseActive: async (centerId) => {
    const response = await warehouseLocationsApiInstance.patch(`/${centerId}/toggle-active`);
    return response.data;
  },

  checkWarehouseStock: async (centerId, productId) => {
    const response = await warehouseLocationsApiInstance.post(`/${centerId}/stock`, {
      product_id: productId,
    });
    return response.data;
  },
};

export { createApiInstance, chatApiInstance, folderApiInstance };
export default api;
